import { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Sparkles, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { currentUser as guestUser } from "@/lib/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tsShort = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleDateString("es", { day: "2-digit", month: "short" });
};

export default function PhotoGallery({ userId, refreshKey = 0 }) {
  const auth = useAuth();
  const meId = auth?.user && typeof auth.user === "object" ? auth.user.id : guestUser.id;
  const targetId = userId || meId;
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/posts/gallery/${targetId}`, { params: { limit: 60 } })
      .then((r) => setPhotos(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [targetId, refreshKey]); // eslint-disable-line

  const remove = async (post) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await axios.delete(`${API}/posts/${post.id}`, { params: { user_id: targetId } });
      setPhotos((p) => p.filter((x) => x.id !== post.id));
      setActive(null);
      toast.success("Foto eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <section data-testid="photo-gallery" className="border hairline bg-cream-card rounded-2xl p-6 md:p-8 mb-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral">tu galería</p>
          <h3 className="font-serif text-3xl tracking-tight mt-1">Mis fotos</h3>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
          {photos.length} {photos.length === 1 ? "foto" : "fotos"}
        </span>
      </div>

      {loading ? (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="gallery-loading">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-cream-deep animate-pulse" />
          ))}
        </div>
      ) : photos.length === 0 ? (
        <div data-testid="gallery-empty" className="text-center py-12">
          <ImageIcon className="w-10 h-10 text-ink-mid mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-sm text-ink-mid">Aún no tienes fotos publicadas.</p>
          <p className="text-xs text-ink-mid mt-1">Publica desde el composer en Inicio para empezar tu galería.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {photos.map((p) => (
            <button
              key={p.id}
              data-testid={`gallery-item-${p.id}`}
              onClick={() => setActive(p)}
              className="relative aspect-square overflow-hidden rounded-xl border hairline group"
            >
              <img
                src={p.photo_data_url}
                alt=""
                style={p.filter_css ? { filter: p.filter_css } : undefined}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {p.signature && (
                <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-coral to-ink text-cream text-[9px] font-mono uppercase tracking-wider">
                  <Sparkles className="w-2 h-2" /> Original
                </span>
              )}
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent text-cream text-[10px] py-1.5 px-2 font-mono opacity-0 group-hover:opacity-100 transition">
                {tsShort(p.created_at)} · ♡ {p.likes || 0}
              </span>
            </button>
          ))}
        </div>
      )}

      {active && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setActive(null)}
          className="fixed inset-0 z-[80] bg-ink/80 backdrop-blur-md flex items-center justify-center p-4"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-cream-card border hairline rounded-2xl overflow-hidden max-w-3xl w-full max-h-[90vh] flex flex-col">
            <header className="flex items-center justify-between px-5 py-3 border-b hairline">
              <div>
                <p className="text-sm font-medium">{active.user_name || "Tú"}</p>
                <p className="text-[11px] font-mono uppercase tracking-wider text-ink-mid">
                  {new Date(active.created_at).toLocaleString("es")}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => remove(active)} data-testid="gallery-delete" className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
                <button onClick={() => setActive(null)} data-testid="gallery-close" className="text-xs px-3 py-1.5 rounded-full bg-ink text-cream hover:bg-coral">
                  Cerrar
                </button>
              </div>
            </header>
            <div className="flex-1 overflow-auto bg-cream-deep flex items-center justify-center p-3">
              <img
                src={active.photo_data_url}
                alt=""
                style={active.filter_css ? { filter: active.filter_css } : undefined}
                className="max-h-[70vh] max-w-full object-contain rounded-md shadow-lg"
              />
            </div>
            {active.text && (
              <div className="px-5 py-3 text-sm border-t hairline whitespace-pre-wrap">{active.text}</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
